import { ModelType } from '@smx-ui-model/types';
import { Select } from 'antd';
import React, { forwardRef, Suspense, useRef, useState } from 'react';

import { EntitySelectOption } from './entity-select-option';
import { SuspenseLoadingStatus } from './suspense-loading-status';
import { DynamicListLoader } from './dynamic-list-loader';
import { DEBOUNCE_DELAY } from '../../../../shared/const';

export interface DynamicListEntityProps<T, K, M> {
    model: ModelType<T>;
    disabled?: boolean;
    listName: string;
    listFieldName: (keyof T)[];
    mode?: 'tags' | 'multiple';
    input?: K;
    style?: React.CSSProperties;
    renderLabel?: (value: any) => React.ReactNode;
    onSelect?: (value: string, data: M) => void

}

//ToDo: need add loading with cursor now pageInfo doesn't include endCursor and hasNextPage is false
export const DynamicListEntity = <T, K, M>() =>
    forwardRef<any, DynamicListEntityProps<T, K, M>>(
        ({ model, listName, listFieldName, style, renderLabel, onSelect, input = {}, ...restProps }, ref) => {
            const [isLoading, setIsLoading] = useState<boolean>(false);
            const [searchString, setSearchString] = useState<string>();
            const [data, setData] = useState<any>();
            const timerRef = useRef<NodeJS.Timeout>(null);

            const newInput = searchString ? {
                ...input,
                filter: {
                    tag: {
                        operator: 'CONTAINS',
                        value: searchString
                    }
                }
            } : input;

            return (
                <>
                    <Suspense
                        fallback={
                            <SuspenseLoadingStatus
                                onStart={() => {
                                    setIsLoading(true);
                                }}
                                onEnd={() => {
                                    setIsLoading(false);
                                }}
                            />
                        }
                    >
                        <DynamicListLoader model={model} input={newInput} onChanged={(data) => {
                            // without timeout get error console
                            setTimeout(() => setData(data));
                        }} />
                    </Suspense>

                    <Select ref={ref} allowClear={false} style={style} onSelect={(value) => {
                        setSearchString('');
                        onSelect?.(value, data?.[listName]);
                    }} {...restProps}
                        loading={isLoading}
                            showSearch={true}
                            onSearch={(value: string) => {
                                if(timerRef.current){
                                    clearTimeout(timerRef.current);
                                }
                                timerRef.current = setTimeout(() => setSearchString(value), DEBOUNCE_DELAY);
                            }}
                    >
                        {data?.[listName]?.edges.map((edge: any) => (
                            <Select.Option key={edge.node.__id} value={edge.node.__id}>
                                {
                                    renderLabel ? renderLabel(edge.node) : <EntitySelectOption node={edge.node} listFieldName={listFieldName} />
                                }
                            </Select.Option>
                        ))}
                    </Select>
                </>
            );
        },
    );
